import { ModeEditOutlined as ModeEditOutlinedIcon } from 'components/icons';
import Avatar from 'components/@extended/Avatar';
import StatusChip from 'components/@extended/StatusChip';
import { RECORD_GRID_CLASS } from 'components/record-header/RecordHeader';
import { Button } from 'components/ui/button';
import { Tooltip } from 'components/ui/tooltip';
import { useModal } from 'components/modals/useModal';
import useAuth from 'hooks/useAuth';
import { useEffectiveDateFormat } from 'hooks/useEffectiveDateFormat';
import { DelegationAlert } from 'pages/delegations/DelegationAlert';
import CaseNoteModal from 'pages/delegations/detail/CaseNoteModal';
import {
    useAddReturnNoteMutation,
    useGetMyDelegationsQuery,
    useGetSubstitutingQuery,
    useUpdateCaseHandoverNoteMutation,
} from 'store/api/delegations';
import { useIsOneDriveIntegrationEnabledQuery } from 'store/api/dmsTeam';
import { useDispatch } from 'store';
import { APIMattersSlice } from 'store/api/matters';
import { errorAlert, successAlert } from 'utils/alert';
import { logError } from 'utils/monitoring';
import AuditInfo from 'components/audit/AuditInfo';
import DisplayHistoryButton from 'components/buttons/DisplayHistoryButton';
import { dmsLinkValue } from 'components/dmslink/DmsLink';
import { DraftSharedWith } from 'components/drafts/DraftSharedWith';
import { DraftActionsContext } from 'components/drafts/useDraftActions';
import { withMissing } from 'components/drafts/withMissing';
import EmployeeName from 'components/employee/EmployeeName';
import GridItemList, { FIELD_GRID_CLASS, GridCells, infoLabel, Row, SectionCard } from 'components/GridItemList';
import InfoTooltip from 'components/InfoTooltip';
import { FormattedDate } from 'components/misc/FormattedDate';
import PartyName from 'components/party/PartyName';
import { RecordLink } from 'components/record-chip/RecordLink';
import { EllipsisTooltip } from 'components/misc/EllipsisTooltip';
import { useIsNewDesign } from 'components/tables/list-table/filter-bar/useIsNewDesign';
import CapFeeBudgetBox from 'pages/invoices/layout/views/components/CapFeeBudgetBox';
import { PricelistDetailsFetchingCard } from 'pages/pricelists/PricelistDetailsFetchingCard';
import React, { useCallback, useContext, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import NumberFormat from 'react-number-format';
import { Link as RouterLink } from 'react-router-dom';
import { TotalAmounts, TotalExpenseAmounts, TotalExpenses, TotalHours, TotalTimecards } from 'sections/stats/Stats.tsx';
import { ClassifierData } from 'types/classifiers';
import { FeatureFlag } from 'types/features.ts';
import { HistoryEntityType, HistoryFieldType } from 'types/histories';
import {
    MatterBillingSummaryDto,
    MatterDto,
    MatterEmployeeRoleType,
    MatterPartyRoleType,
    MatterStateType,
    RemunerationCalcType,
} from 'types/matters';
import { PracticeArea, PracticeAreaType } from 'types/practice-areas';
import { Fetchable, ScopeType } from 'types/pricelists';
import { ReferralTypeCode } from 'types/referrals';
import { cn } from 'utils/cn';
import { isMatterClosed } from 'utils/matterState';
import { getValidOptionsFromClassifier } from 'utils/formUtil';
import { getClassifierText, t } from 'utils/getTranslations';
import { nameInitials } from 'utils/nameOrder';
import { formatPracticeAreaLabel } from 'utils/practiceAreaUtil';
import { useIsFeatureEnabled } from 'utils/useFeatureFlags.ts';
import { useHasPermissions } from 'utils/permissionUtil';
import { AmlMark } from '../components/AmlMark';
import { ConnectedCase, ConnectedCaseLink } from '../components/ConnectedCase';
import { MatterSection } from '../form-sections/MatterSection';
import { useMatterFieldGates } from '../form-sections/useMatterForm';
import { teamMembers } from '../matterTeamMembers';
import { ENTITY_LINK_CLASS, formatSubstitutePeriod, getLinkChips, getLinks } from '../util';
import { useAdministratorField } from 'hooks/useAdministratorField';

const STAT_CARD_CLASS = 'rounded-[7px]';
const STAT_CONTENT_CLASS = 'p-3';

interface Props {
    classifiers: Record<string, ClassifierData>;
    matter: MatterDto;
    matterBillingSummary?: MatterBillingSummaryDto;
}

/**
 * Handover-notes cell on the case overview. Read-only for everyone (sourced from the matter, which the backend fills
 * from the delegation cover); the delegator additionally gets a pen while the delegation is still pending/upcoming,
 * writing the note through the delegation endpoint rather than the matter form.
 */
const DelegationHandoverNotesContent = ({ matter }: { matter: MatterDto }): React.ReactNode => {
    const { user } = useAuth();
    const { openModal, closeModal } = useModal();
    const [updateHandover] = useUpdateCaseHandoverNoteMutation();
    // Only the matter's own owners can be the delegator; skip the fetch for everyone else.
    const isPossibleDelegator =
        matter.supervisedByEmployee?.id === user?.id || matter.responsibleEmployee?.id === user?.id;
    const { data: myDelegations } = useGetMyDelegationsQuery(
        { scope: 'OWN' },
        { skip: !matter.hasNonTerminalDelegation || !isPossibleDelegator }
    );

    // Handover is editable by the delegator only while the delegation is still pending/upcoming (read-only once active).
    const mine = myDelegations?.find(
        (d) =>
            (d.status === 'PENDING' || d.status === 'UPCOMING') &&
            d.delegator.id === user?.id &&
            d.cases.some((c) => c.matter.id === matter.id)
    );
    const myCases = mine?.cases.filter((c) => c.matter.id === matter.id);
    const note = myCases?.length ? (myCases[0].handoverNote ?? '') : (matter.delegationHandoverNotes ?? '');

    const onEdit = (): void =>
        openModal(
            <CaseNoteModal
                titleId="delegation.notes.edit-handover-title"
                labelId="delegation.notes.handover-label"
                initialValue={note}
                onCancel={closeModal}
                onSave={(value) => {
                    closeModal();
                    const [first, ...others] = myCases!;
                    void updateHandover({
                        id: mine!.id,
                        caseId: first.id,
                        otherCaseIds: others.map((c) => c.id),
                        note: value,
                    })
                        .unwrap()
                        .then(() => successAlert(t('delegation.toast.handover-saved')))
                        .catch((err) => {
                            logError(err);
                            errorAlert(t('delegation.toast.error'));
                        });
                }}
            />
        );

    return (
        <div className="flex items-start gap-1">
            <span className="whitespace-pre-wrap">{note || '-'}</span>
            {myCases?.length ? (
                <Tooltip content={t('edit')}>
                    <Button variant="ghost" size="icon-xs" aria-label={t('edit')} onClick={onEdit}>
                        <ModeEditOutlinedIcon className="text-grey-500 size-4" />
                    </Button>
                </Tooltip>
            ) : null}
        </div>
    );
};

/** Who may list their own covers, which the substitute's pen needs once the case no longer names them. */
const VIEW_OWN_DELEGATIONS = ['VIEW.DELEGATIONS.MY_OWN'];

/**
 * Return-notes cell on the case overview. Read-only for everyone (sourced from the matter, which the backend fills
 * from the active cover, or after the return from the latest delegation that left notes); the substitute
 * additionally gets a pen to edit, since they cannot reach the matter edit form and must write the note through the
 * delegation endpoint. A note added after the return says when.
 */
const DelegationReturnNotesContent = ({ matter }: { matter: MatterDto }): React.ReactNode => {
    const { user } = useAuth();
    const dispatch = useDispatch();
    const { openModal, closeModal } = useModal();
    const [addReturnNote] = useAddReturnNoteMutation();
    const active = !!matter.hasActiveDelegation;
    const canListOwnCovers = useHasPermissions(VIEW_OWN_DELEGATIONS);
    // While active only the matter's assigned substitutes need the live cover; once returned the matter no longer
    // names them.
    const isPossibleSubstitute = active
        ? matter.substituteSupervisingPartner?.employee.id === user?.id ||
          matter.substituteResponsibleLawyer?.employee.id === user?.id
        : canListOwnCovers;
    const { data: substituting } = useGetSubstitutingQuery({ scope: 'OWN' }, { skip: !isPossibleSubstitute });

    type CoverCase = NonNullable<typeof substituting>[number]['cases'][number];
    const isMyCase = (c: CoverCase): boolean =>
        c.matter.id === matter.id &&
        c.substitute.id === user?.id &&
        (active ? !c.returnedAt : !!c.returnedAt && !!c.returnNote?.trim());
    // After the return, the latest returned cover the substitute left a note on.
    const mine = substituting
        ?.filter((d) => (!active || d.status === 'ACTIVE') && d.cases.some(isMyCase))
        .sort((a, b) => b.endDate.localeCompare(a.endDate))[0];
    const myCases = mine?.cases.filter(isMyCase);
    // While active the substitute reads their live note straight off the cover (auto-refreshes on save); everyone
    // else, and everyone after the return, the copy exposed on the matter.
    const note = active && myCases?.length ? (myCases[0].returnNote ?? '') : (matter.delegationReturnNotes ?? '');

    const onEdit = (): void =>
        openModal(
            <CaseNoteModal
                titleId="delegation.notes.edit-return-title"
                labelId="delegation.notes.return-label"
                initialValue={myCases![0].returnNote ?? ''}
                saveId="delegation.actions.submit-return-notes"
                onCancel={closeModal}
                onSave={(value) => {
                    closeModal();
                    const [first, ...others] = myCases!;
                    void addReturnNote({
                        id: mine!.id,
                        caseId: first.id,
                        otherCaseIds: others.map((c) => c.id),
                        note: value,
                    })
                        .unwrap()
                        .then(() => {
                            dispatch(APIMattersSlice.util.invalidateTags([{ type: 'Matter', id: String(matter.id) }]));
                            successAlert(t('delegation.toast.return-note-saved'));
                        })
                        .catch((err) => {
                            logError(err);
                            errorAlert(t('delegation.toast.error'));
                        });
                }}
            />
        );

    return (
        <div className="flex items-start gap-1">
            <div className="flex min-w-0 flex-col gap-1">
                <span className="whitespace-pre-wrap">{note || '-'}</span>
                {matter.delegationReturnNotesAddedAt ? (
                    <span className="text-muted-foreground text-xs">
                        <FormattedMessage
                            id="matters.return-note-added"
                            values={{ date: <FormattedDate key="date" value={matter.delegationReturnNotesAddedAt} /> }}
                        />
                    </span>
                ) : null}
            </div>
            {myCases?.length ? (
                <Tooltip content={t('edit')}>
                    <Button variant="ghost" size="icon-xs" aria-label={t('edit')} onClick={onEdit}>
                        <ModeEditOutlinedIcon className="text-grey-500 size-4" />
                    </Button>
                </Tooltip>
            ) : null}
        </div>
    );
};

const HOURS_OFFER_VALUE_TYPE = 'HOURS';

function toFiniteNumber(value: number | string | null | undefined): number | undefined {
    if (value === null || value === undefined || value === '') {
        return undefined;
    }
    const numericValue = Number(value);
    return Number.isFinite(numericValue) ? numericValue : undefined;
}

function EstimateFigure({ value }: { value: number }): JSX.Element {
    return (
        <NumberFormat
            displayType="text"
            value={value}
            thousandSeparator={' '}
            decimalScale={2}
            fixedDecimalScale={false}
        />
    );
}

function OfferValue({ matter, classifiers }: Pick<Props, 'matter' | 'classifiers'>): JSX.Element {
    const low = toFiniteNumber(matter.offerValueLow);
    const high = toFiniteNumber(matter.offerValueHigh);
    const from = low ?? high;

    if (from === undefined) {
        return <>-</>;
    }

    // Cash is the default type, so a value stored without a type is a monetary amount.
    const unit =
        matter.offerValueTypeCode === HOURS_OFFER_VALUE_TYPE
            ? getClassifierText(classifiers['OFFER_VALUE_TYPE'], matter.offerValueTypeCode)
            : matter.pricelistCurrencyTypeCode;
    const to = low !== undefined && high !== undefined && high !== low ? high : undefined;

    return (
        <span className="whitespace-nowrap">
            <EstimateFigure value={from} />
            {to !== undefined ? (
                <>
                    {' – '}
                    <EstimateFigure value={to} />
                </>
            ) : null}
            {unit ? ` ${unit}` : null}
        </span>
    );
}

function EstimatedEngagementLength({ matter, classifiers }: Pick<Props, 'matter' | 'classifiers'>): JSX.Element {
    const length = toFiniteNumber(matter.estimatedEngagementLength);

    if (length === undefined) {
        return <>-</>;
    }

    const unit = getClassifierText(
        classifiers['ENGAGEMENT_LENGTH_UNIT'],
        matter.estimatedEngagementUnitCode ?? undefined
    );

    return (
        <span className="whitespace-nowrap">
            <EstimateFigure value={length} />
            {unit ? ` ${unit}` : null}
        </span>
    );
}

const TEAM_SHOWN = 5;

const TILE_CLASS = 'flex min-w-0 items-start gap-2.5 rounded-lg border px-3 py-2.5 no-underline';

/** The teams under Our team: no field edits them, so the row stays as text while the section edits. */
const teamRows = (matter: MatterDto): Row[] => [
    {
        content: getLinks('team', matter.teams ?? []),
        label: t('matters.teams'),
        wide: true,
        hidden: !matter.teams?.length,
        readOnly: true,
    },
];

/** Our team in the new design: one tile per person, five shown with Show all for the rest, then the teams. */
const TeamCard = ({
    matter,
    caseAssistantLabel,
    edit,
}: {
    matter: MatterDto;
    /** The label of the case assistants told apart from Handled by; null keeps them all under Handled by. */
    caseAssistantLabel: string | null;
    edit: React.ReactNode;
}): JSX.Element => {
    const [showAll, setShowAll] = useState(false);
    const dateFormat = useEffectiveDateFormat();
    const members = teamMembers(matter, caseAssistantLabel, dateFormat);
    // A Show all tile that would hide a single person takes that person's place instead.
    const folded = !showAll && members.length > TEAM_SHOWN + 1;
    const shown = folded ? members.slice(0, TEAM_SHOWN) : members;
    return (
        <SectionCard
            title={
                <>
                    <FormattedMessage id="matters.our-team" />
                    {members.length > 0 ? (
                        <span className="text-muted-foreground font-medium"> · {members.length}</span>
                    ) : null}
                </>
            }
            action={edit}
        >
            {members.length === 0 ? <span>-</span> : null}
            <div className="grid grid-cols-1 gap-2.5 @2xs:grid-cols-2 @2xl:grid-cols-3">
                {shown.map(({ employee, roles }) => (
                    <RouterLink
                        key={employee.id}
                        to={`/employee/${employee.id}`}
                        className={cn(TILE_CLASS, 'border-neutral-tint text-foreground hover:bg-surface-subtle')}
                    >
                        <Avatar className="bg-primary-tint text-primary-ink size-[30px] flex-none text-xs font-semibold">
                            {nameInitials(employee)}
                        </Avatar>
                        <span className="flex min-w-0 flex-col leading-[1.3]">
                            <span className="line-clamp-2 font-medium">
                                <EmployeeName entity={employee} />
                            </span>
                            {roles.map((role) => (
                                <span key={role} className="text-muted-foreground text-xs">
                                    {role}
                                </span>
                            ))}
                        </span>
                    </RouterLink>
                ))}
                {folded ? (
                    <button
                        type="button"
                        onClick={() => setShowAll(true)}
                        className={cn(
                            TILE_CLASS,
                            'border-border-dashed text-primary cursor-pointer items-center border-dashed bg-transparent font-medium'
                        )}
                    >
                        <span className="bg-segmented text-grey-600 flex size-[30px] flex-none items-center justify-center rounded-full text-xs font-semibold">
                            +{members.length - TEAM_SHOWN}
                        </span>
                        <FormattedMessage id="sections.show-all" values={{ count: members.length }} />
                    </button>
                ) : null}
            </div>
            {matter.teams?.length ? (
                <dl className={cn(FIELD_GRID_CLASS, 'm-0')}>
                    <GridCells rows={teamRows(matter)} />
                </dl>
            ) : null}
        </SectionCard>
    );
};

function ConfidentialReason({ matter }: { matter: MatterDto }) {
    const { confidentialReason, confidentialReasonRecordedBy, confidentialReasonRecordedAt } = matter;
    return (
        <div className="flex flex-col gap-1">
            <span className="whitespace-pre-line">{confidentialReason}</span>
            {confidentialReasonRecordedBy && confidentialReasonRecordedAt ? (
                <span className="text-muted-foreground text-xs font-normal">
                    <FormattedMessage
                        id="case-closing-confidentiality.recorded-by"
                        values={{
                            name: confidentialReasonRecordedBy.name,
                            date: <FormattedDate key="date" value={confidentialReasonRecordedAt} />,
                        }}
                    />
                </span>
            ) : null}
        </div>
    );
}

const GeneralView: React.FC<Props> = ({ matter, classifiers, matterBillingSummary }) => {
    const isNew = useIsNewDesign();
    const dateFormat = useEffectiveDateFormat();
    const missingFields = useContext(DraftActionsContext)?.draft?.missingFields;
    const isRemunerationCalcTypeControlEnabled = useIsFeatureEnabled(FeatureFlag.REMUNERATION_CALC_TYPE_CONTROL);
    const isOfficialRefNumberEnabled = useIsFeatureEnabled(FeatureFlag.CASE_OFFICIAL_REF_NUMBER_FIELD);
    const isTimecardIncrementControlEnabled = useIsFeatureEnabled(FeatureFlag.TIMECARD_INCREMENT_CONTROL);

    const isOfferFlowEnabled = useIsFeatureEnabled(FeatureFlag.CASE_OFFER_FLOW);
    const { isOn: isAdministratorFieldEnabled, caseLabel: caseAssistantLabel } = useAdministratorField();
    const isOfferLost = isOfferFlowEnabled && matter.matterStateTypeCode === MatterStateType.OFFER_LOST;
    const hasOfferOutcome =
        isOfferFlowEnabled &&
        (matter.matterStateTypeCode === MatterStateType.OFFER_LOST ||
            matter.matterStateTypeCode === MatterStateType.OFFER_WON);
    const isOfferStage =
        isOfferFlowEnabled && (hasOfferOutcome || matter.matterStateTypeCode === MatterStateType.OFFER_PENDING);
    const hasOfferValue =
        toFiniteNumber(matter.offerValueLow) !== undefined || toFiniteNumber(matter.offerValueHigh) !== undefined;
    const hasEstimatedEngagementLength = toFiniteNumber(matter.estimatedEngagementLength) !== undefined;
    // Firm-defined taxonomy: the row disappears entirely for a tenant that never populated the classifier. A case
    // that carries a type keeps the row even once the firm retires every value, so a stored value stays readable.
    const showMatterType =
        getValidOptionsFromClassifier(classifiers['MATTER_TYPE']).length > 0 || matter.matterTypeCode != null;
    const isSinglePracticeArea = useIsFeatureEnabled(FeatureFlag.CASE_PA_FIELD_SINGLE_SELECTION_ONLY);
    // What decides whether the case form shows a field, for the new design's rows to follow.
    const isSubPracticeAreaEnabled = useIsFeatureEnabled(FeatureFlag.SUB_PRACTICE_AREA);
    const { data: isOneDriveIntegrationEnabled } = useIsOneDriveIntegrationEnabledQuery(undefined, { skip: !isNew });
    // The form has the practice area for DMS unless OneDrive is on, so with an answer that fails too. `MatterPage`
    // holds its loader until the answer is in: a row that goes, or comes late, would move the card under the reader.
    const hidesDmsPracticeArea = isOneDriveIntegrationEnabled === true;
    const { shouldShowRemunerationField, canReadInsiderInfo, canUpdateInsiderInfo } = useMatterFieldGates();
    const isInsiderListEnabled = useIsFeatureEnabled(FeatureFlag.MATTER_INSIDER_LIST);
    const showsInsiderInfo = isInsiderListEnabled && canReadInsiderInfo && canUpdateInsiderInfo;

    const getOverviewData = useCallback((): Row[] => {
        const matterType: Row = {
            content: matter.matterTypeCode ? getClassifierText(classifiers['MATTER_TYPE'], matter.matterTypeCode) : '-',
            label: t('matters.case-type'),
            hidden: !showMatterType,
        };
        const office: Row = {
            content: matter.handledByOffice ? (
                <RecordLink
                    className={ENTITY_LINK_CLASS}
                    to={`/office/${matter.handledByOffice.id}`}
                    label={matter.handledByOffice.name}
                />
            ) : (
                '-'
            ),
            label: t('matters.handled-by-office'),
        };
        const confidential: Row = {
            content: matter.isConfidential ? t('yes') : t('no'),
            label: t('matters.is-confidential'),
        };
        const insider: Row = {
            content: matter.isInsider ? t('yes') : t('no'),
            label: t('matters.is-insider'),
            hidden: !(matter.isConfidential && showsInsiderInfo),
        };
        const confidentialReason: Row = {
            content: <ConfidentialReason matter={matter} />,
            label: t('case-closing-confidentiality.reason-label'),
            hidden: !(matter.isConfidential && isMatterClosed(matter.matterStateTypeCode) && matter.confidentialReason),
            wide: true,
        };
        const firmWide: Row = {
            content: matter.isAccessibleFirmWide ? t('yes') : t('no'),
            label: isNew
                ? infoLabel(
                      'matters.enable-firm-wide-access',
                      'matters.enable-firm-wide-access-tooltip',
                      'justify-between'
                  )
                : t('matters.is-accessible-firm-wide'),
            hidden: !matter.isInternal,
        };
        const description: Row = {
            content: matter.description || '-',
            label: t('matters.description'),
            multiline: true,
        };
        const practiceAreas: Row = {
            content: matter.practiceAreas?.length ? (
                <div className="flex flex-col gap-1">
                    {matter.practiceAreas
                        .toSorted((a, b) => formatPracticeAreaLabel(a).localeCompare(formatPracticeAreaLabel(b)))
                        .map((item) => (
                            <div key={item.id} className="flex items-center gap-2">
                                <span>{formatPracticeAreaLabel(item)}</span>
                                {item.isAmlApplicable && (
                                    <Tooltip content={t('matters.aml-applicable-tooltip')}>
                                        <span>
                                            <StatusChip type="warning" label={t('matters.aml-applicable')} />
                                        </span>
                                    </Tooltip>
                                )}
                            </div>
                        ))}
                </div>
            ) : (
                '-'
            ),
            label: t(isSinglePracticeArea ? 'practice-area' : 'practice-areas.practice-areas'),
            wide: true,
        };
        const serviceAgreement: Row = {
            content: matter.serviceAgreementContract ? (
                // In the new design the cell keeps the line under the link whether or not a price list fills it:
                // the form has its View link there.
                <div className={cn('flex flex-col', isNew && 'min-h-12')}>
                    <RecordLink
                        className={ENTITY_LINK_CLASS}
                        to={`/contract/${matter.serviceAgreementContract.id}`}
                        label={matter.serviceAgreementContract.contractName}
                    />
                    {matter.serviceAgreementContract.pricelistId && (
                        <div className={cn('mt-1', isNew && 'flex h-6 items-center')}>
                            <PricelistDetailsFetchingCard
                                simpleView
                                pricelist={
                                    {
                                        id: matter.serviceAgreementContract.pricelistId,
                                        name: matter.serviceAgreementContract.pricelistName ?? '',
                                        scopeTypeCode: ScopeType.CONTRACT,
                                        currencyTypeCode: matter.serviceAgreementContract.pricelistCurrencyTypeCode,
                                        minHourlyPrice: matter.serviceAgreementContract.minHourlyPrice,
                                        maxHourlyPrice: matter.serviceAgreementContract.maxHourlyPrice,
                                        avgHourlyPrice: matter.serviceAgreementContract.avgHourlyPrice,
                                    } as Fetchable
                                }
                            />
                        </div>
                    )}
                </div>
            ) : (
                '-'
            ),
            label: t('matters.service-agreement'),
            wide: true,
        };
        const openedDate: Row = {
            content: <FormattedDate value={matter.openedDate} />,
            label: t('matters.opening-date'),
        };
        const closedDate: Row = {
            content: <FormattedDate value={matter.closedDate} />,
            label: t('matters.closing-date'),
        };
        const timecardIncrement: Row = {
            content: matter.timecardIncrement?.toString() || '-',
            label: t('matters.timecard-increment'),
            hidden: !isTimecardIncrementControlEnabled,
        };
        // The form shows both estimates together, on a case in an offer stage or carrying either.
        const hidesEstimates = !isOfferStage && !hasOfferValue && !hasEstimatedEngagementLength;
        const offerValue: Row = {
            content: <OfferValue matter={matter} classifiers={classifiers} />,
            label: (
                <div className="flex items-center gap-1">
                    {t('matters.offer-value')}
                    <InfoTooltip text="matters.offer-value-tooltip" />
                </div>
            ),
            hidden: !isOfferFlowEnabled || (isNew ? hidesEstimates : !isOfferStage && !hasOfferValue),
            // A row of its own in the new design, as the form's amount and unit controls take one.
            wide: true,
        };
        const estimatedEngagement: Row = {
            content: <EstimatedEngagementLength matter={matter} classifiers={classifiers} />,
            label: (
                <div className="flex items-center gap-1">
                    {t('matters.estimated-engagement-length')}
                    <InfoTooltip text="matters.estimated-engagement-length-tooltip" />
                </div>
            ),
            hidden: !isOfferFlowEnabled || (isNew ? hidesEstimates : !isOfferStage && !hasEstimatedEngagementLength),
            wide: true,
        };
        const reasonOfferLost: Row = {
            content: matter.reasonOfferLostCode
                ? getClassifierText(classifiers['REASON_OFFER_LOST'], matter.reasonOfferLostCode)
                : '-',
            label: isNew
                ? infoLabel('matters.reason-offer-lost', 'matters.reason-offer-lost-tooltip')
                : t('matters.reason-offer-lost'),
            hidden: !isOfferLost,
        };
        const outcomeNotes: Row = {
            content: matter.outcomeNotes || '-',
            label: isNew
                ? infoLabel('matters.outcome-notes', 'matters.outcome-notes-tooltip')
                : t('matters.outcome-notes'),
            multiline: true,
            hidden: !hasOfferOutcome,
        };
        const connectedCase: Row = {
            content: matter.connectedCase && (
                <ConnectedCase
                    offerCaseId={matter.id}
                    connectedCase={matter.connectedCase}
                    matterStateTypes={classifiers['MATTER_STATE_TYPE']}
                    isUnlinkEnabled={matter.isEditable}
                />
            ),
            label: t('matters.connected-case'),
            hidden: !isOfferFlowEnabled || !matter.connectedCase,
            readOnly: true,
        };
        const originatingCase: Row = {
            content: matter.originatesFromMatter && (
                <ConnectedCaseLink
                    connectedCase={matter.originatesFromMatter}
                    matterStateTypes={classifiers['MATTER_STATE_TYPE']}
                />
            ),
            label: t('matters.connected-case'),
            hidden: !matter.originatesFromMatter,
            readOnly: true,
        };
        const generalNotes: Row = {
            content: matter.generalNotes || '-',
            label: t('matters.general-notes'),
            wide: true,
            // As its text area shows it. Classic's row runs the lines together.
            multiline: isNew,
        };
        const handoverNotes: Row = {
            // Shown for the whole delegation window (pending → active); the delegator can edit while
            // pending/upcoming. Hidden once the delegation is terminal.
            content: <DelegationHandoverNotesContent matter={matter} />,
            label: t('matters.delegation-handover-notes'),
            multiline: true,
            hidden: !matter.hasNonTerminalDelegation,
            readOnly: true,
        };
        const returnNotes: Row = {
            // Shown for the whole active-delegation window so the field is visible even before the
            // substitute enters anything, and after the return for as long as the case's notes last.
            content: <DelegationReturnNotesContent matter={matter} />,
            label: t('matters.delegation-return-notes'),
            multiline: true,
            hidden: !matter.hasActiveDelegation && !matter.delegationReturnNotes,
            readOnly: true,
        };
        const officialReference: Row = {
            content: matter.officialReference || '-',
            label: isNew
                ? infoLabel(
                      'matters.official-reference-number',
                      'matters.tooltips.official-reference-number',
                      'justify-between'
                  )
                : t('matters.official-reference-number'),
            hidden: !isOfficialRefNumberEnabled,
        };
        const project: Row = {
            content:
                matter.project != null
                    ? getLinks('projects', [
                          {
                              id: matter.project.id,
                              name: matter.project.projectName,
                          },
                      ])
                    : '-',
            label: t('projects.projects'),
        };
        const remuneration: Row = {
            content: matter.remunerationCalcTypeCode === RemunerationCalcType.EQUALS_USED_TIME ? t('yes') : t('no'),
            label: t('matters.use-used-time-for-remuneration'),
            hidden: !isRemunerationCalcTypeControlEnabled,
            // The form leaves the field out for a user who may neither set nor see the remuneration time.
            readOnly: !shouldShowRemunerationField,
        };
        if (!isNew) {
            return [
                matterType,
                office,
                confidential,
                confidentialReason,
                firmWide,
                description,
                practiceAreas,
                serviceAgreement,
                openedDate,
                closedDate,
                timecardIncrement,
                offerValue,
                estimatedEngagement,
                reasonOfferLost,
                outcomeNotes,
                connectedCase,
                originatingCase,
                generalNotes,
                handoverNotes,
                returnNotes,
                officialReference,
                project,
                remuneration,
            ];
        }

        // The new design lists the form's fields in the form's order, each under the form's label and where the
        // form shows it, so a row and its field share a cell. The rows no field edits follow, as `readOnly` has it.
        const areaChips = (areas: PracticeArea[]) =>
            areas.map((area) => ({
                key: area.id,
                label: formatPracticeAreaLabel(area),
                adornment: area.isAmlApplicable ? <AmlMark /> : null,
            }));
        const areas = matter.practiceAreas ?? [];
        const subAreas = areas.filter((area) => area.practiceAreaTypeCode === PracticeAreaType.PRACTICE_SUBAREA);
        return [
            { content: matter.name, label: t('matters.name') },
            {
                content: matter.clientParty ? (
                    <EllipsisTooltip>
                        <PartyName party={matter.clientParty} isLink variant="readonly-other" singleLine hideTitle />
                    </EllipsisTooltip>
                ) : (
                    '-'
                ),
                label: t('matters.connected-client'),
            },
            matterType,
            {
                content: getClassifierText(classifiers['MATTER_STATE_TYPE'], matter.matterStateTypeCode),
                label: t('matters.state'),
            },
            reasonOfferLost,
            outcomeNotes,
            offerValue,
            estimatedEngagement,
            office,
            description,
            remuneration,
            confidential,
            insider,
            confidentialReason,
            {
                content: matter.insiderInfo || '-',
                label: t('matters.insider-info'),
                multiline: true,
                hidden: !(matter.isConfidential && showsInsiderInfo),
            },
            firmWide,
            {
                ...practiceAreas,
                label: t(isSinglePracticeArea ? 'practice-area' : 'matters.practice-areas'),
                content: '-',
                // With the 2nd level on, that field holds the sub-areas; without it this row keeps them all.
                chips: areaChips(isSubPracticeAreaEnabled ? areas.filter((area) => !subAreas.includes(area)) : areas),
            },
            {
                content: '-',
                label: t('matters.second-level-practice-areas'),
                chips: areaChips(subAreas.map((area) => ({ ...area, name: area.fullPath }))),
                hidden: !isSubPracticeAreaEnabled,
            },
            {
                content: matter.dmsPracticeArea
                    ? formatPracticeAreaLabel({ ...matter.dmsPracticeArea, name: matter.dmsPracticeArea.fullPath })
                    : '-',
                label: infoLabel('matters.practice-area-for-dms', 'matters.tooltips.practice-area-for-dms'),
                hidden: hidesDmsPracticeArea,
            },
            serviceAgreement,
            timecardIncrement,
            project,
            openedDate,
            closedDate,
            generalNotes,
            officialReference,
            {
                content: matter.dmsLink ? dmsLinkValue(matter.dmsLink) : '-',
                label: t('matters.dms-link'),
            },
            connectedCase,
            originatingCase,
            handoverNotes,
            returnNotes,
        ];
    }, [
        matter,
        classifiers,
        isNew,
        isOfferFlowEnabled,
        isOfferLost,
        hasOfferOutcome,
        isOfferStage,
        hasOfferValue,
        hasEstimatedEngagementLength,
        isOfficialRefNumberEnabled,
        isRemunerationCalcTypeControlEnabled,
        isTimecardIncrementControlEnabled,
        showMatterType,
        shouldShowRemunerationField,
        showsInsiderInfo,
        isSubPracticeAreaEnabled,
        isSinglePracticeArea,
        hidesDmsPracticeArea,
    ]);

    const getEmployeeData = useCallback(
        (): Row[] => [
            {
                content: (
                    <div className="flex items-center gap-[5px]">
                        {matter.supervisedByEmployee ? (
                            <RouterLink
                                className={ENTITY_LINK_CLASS}
                                to={`/employee/${matter.supervisedByEmployee.id}`}
                            >
                                <EmployeeName entity={matter.supervisedByEmployee} />
                            </RouterLink>
                        ) : (
                            '-'
                        )}
                    </div>
                ),
                label: (
                    <div className="flex gap-[.5ch]">
                        <FormattedMessage id="matters.supervised-by" />
                        {matter.isDraft ? null : (
                            <DisplayHistoryButton
                                entityType={HistoryEntityType.MATTER}
                                entityId={`${matter.id}`}
                                fieldType={HistoryFieldType.SUPERVISING_EMPLOYEE}
                                fieldName={t('matters.supervised-by')}
                                activity={t('updating')}
                            />
                        )}
                    </div>
                ),
            },
            {
                hidden: !matter.substituteSupervisingPartner,
                content: matter.substituteSupervisingPartner ? (
                    <div className="flex items-center gap-1">
                        <RouterLink
                            className={ENTITY_LINK_CLASS}
                            to={`/employee/${matter.substituteSupervisingPartner.employee.id}`}
                        >
                            {matter.substituteSupervisingPartner.employee.name}
                        </RouterLink>
                        <span className="text-fg-secondary">
                            ·{' '}
                            {formatSubstitutePeriod(
                                matter.substituteSupervisingPartner.startDate,
                                matter.substituteSupervisingPartner.endDate,
                                dateFormat
                            )}
                        </span>
                    </div>
                ) : (
                    '-'
                ),
                label: t('matters.substitute-supervising-partner'),
            },
            {
                content: (
                    <div className="flex items-center gap-[.5ch]">
                        {matter.responsibleEmployee?.name ? (
                            <RouterLink className={ENTITY_LINK_CLASS} to={`/employee/${matter.responsibleEmployee.id}`}>
                                <EmployeeName entity={matter.responsibleEmployee} />
                            </RouterLink>
                        ) : (
                            '-'
                        )}
                    </div>
                ),
                label: (
                    <div className="flex gap-[.5ch]">
                        <FormattedMessage id="matters.responsible-lawyer" />
                        {matter.isDraft ? null : (
                            <DisplayHistoryButton
                                entityType={HistoryEntityType.MATTER}
                                entityId={`${matter.id}`}
                                fieldType={HistoryFieldType.RESPONSIBLE_EMPLOYEE}
                                fieldName={t('matters.responsible-lawyer')}
                                activity={t('updating')}
                            />
                        )}
                    </div>
                ),
            },
            {
                hidden: !matter.substituteResponsibleLawyer,
                content: matter.substituteResponsibleLawyer ? (
                    <div className="flex items-center gap-1">
                        <RouterLink
                            className={ENTITY_LINK_CLASS}
                            to={`/employee/${matter.substituteResponsibleLawyer.employee.id}`}
                        >
                            {matter.substituteResponsibleLawyer.employee.name}
                        </RouterLink>
                        <span className="text-fg-secondary">
                            ·{' '}
                            {formatSubstitutePeriod(
                                matter.substituteResponsibleLawyer.startDate,
                                matter.substituteResponsibleLawyer.endDate,
                                dateFormat
                            )}
                        </span>
                    </div>
                ) : (
                    '-'
                ),
                label: t('matters.substitute-responsible-lawyer'),
            },
            {
                content: (() => {
                    const employeeReferrals = matter.referrals?.filter(
                        (r) => r.referralTypeCode === ReferralTypeCode.EMPLOYEE && r.employee
                    );
                    if (employeeReferrals && employeeReferrals.length > 0) {
                        return getLinks(
                            'employee',
                            employeeReferrals.map((r) => r.employee!)
                        );
                    }
                    if (matter.referredByEmployee?.name) {
                        return (
                            <RouterLink className={ENTITY_LINK_CLASS} to={`/employee/${matter.referredByEmployee.id}`}>
                                <EmployeeName entity={matter.referredByEmployee} />
                            </RouterLink>
                        );
                    }
                    return '-';
                })(),
                label: (
                    <div className="flex items-center gap-[.5ch]">
                        {t('matters.referred-by')}
                        <InfoTooltip text="matters.referred-by-tooltip" />
                    </div>
                ),
            },
            ...(isAdministratorFieldEnabled
                ? [
                      {
                          content: matter.employees
                              ? getLinks(
                                    'employee',
                                    matter.employees
                                        .filter(
                                            (item) =>
                                                item.matterEmployeeRoleTypeCode === MatterEmployeeRoleType.STAFF &&
                                                item.administrator
                                        )
                                        .map((item) => item.employee)
                                )
                              : '-',
                          label: caseAssistantLabel,
                      },
                  ]
                : []),
            {
                content: matter.employees
                    ? getLinks(
                          'employee',
                          matter.employees
                              .filter(
                                  (item) =>
                                      item.matterEmployeeRoleTypeCode === MatterEmployeeRoleType.STAFF &&
                                      // Only split the assistants out when there is a row rendering
                                      // them; otherwise they would show in neither list.
                                      !(isAdministratorFieldEnabled && item.administrator)
                              )
                              .map((item) => item.employee)
                      )
                    : '-',
                label: t('matters.handled-by'),
            },
            {
                content: matter.teams ? getLinks('team', matter.teams) : '-',
                label: t('matters.teams'),
            },
        ],
        [matter, isAdministratorFieldEnabled, caseAssistantLabel, dateFormat]
    );

    // A party list as its multi-select holds it: the case's order, a chip per contact. Only the new design's
    // grid draws a row's chips.
    const partyChips = useCallback(
        (role: MatterPartyRoleType) =>
            getLinkChips(
                'contact',
                (matter.parties ?? []).filter((item) => item.matterPartyRoleTypeCode === role).map((item) => item.party)
            ),
        [matter.parties]
    );

    const getClientData = useCallback((): Row[] => {
        const primaryContact = matter.primaryContactParty && (
            <PartyName party={matter.primaryContactParty} isLink variant="readonly-other" singleLine={isNew} />
        );
        return [
            {
                // One line in the new design, as its select shows it.
                content: !primaryContact ? (
                    '-'
                ) : isNew ? (
                    <EllipsisTooltip>{primaryContact}</EllipsisTooltip>
                ) : (
                    primaryContact
                ),
                label: t('matters.primary-contact'),
            },
            {
                content: matter.parties
                    ? getLinks(
                          'contact',
                          matter.parties
                              .filter((item) => item.matterPartyRoleTypeCode === MatterPartyRoleType.CLIENT_REPRESENTED)
                              .map((item) => item.party)
                      )
                    : '-',
                label: t('matters.represented-clients'),
                chips: partyChips(MatterPartyRoleType.CLIENT_REPRESENTED),
            },
        ];
    }, [matter, isNew, partyChips]);

    const getOtherData = useCallback(
        (): Row[] =>
            [
                // Classic answers "No opponent" in a row of its own and hides the opponents' row on a yes. The
                // new design says "None" where the opponents would be.
                ...(isNew
                    ? []
                    : [{ content: matter.isNoOpponent ? t('yes') : t('no'), label: t('matters.no-opponent') }]),
                {
                    content:
                        isNew && matter.isNoOpponent
                            ? t('matters.opponents-none')
                            : matter.parties
                              ? getLinks(
                                    'contact',
                                    matter.parties
                                        .filter((item) => item.matterPartyRoleTypeCode === MatterPartyRoleType.OPPONENT)
                                        .map((item) => item.party)
                                )
                              : '-',
                    label: t('matters.opponents'),
                    chips: partyChips(MatterPartyRoleType.OPPONENT),
                },
                {
                    content: matter.parties
                        ? getLinks(
                              'contact',
                              matter.parties
                                  .filter((item) => item.matterPartyRoleTypeCode === MatterPartyRoleType.TARGET)
                                  .map((item) => item.party)
                          )
                        : '-',
                    label: t('matters.targets'),
                    chips: partyChips(MatterPartyRoleType.TARGET),
                },
                {
                    content: matter.parties
                        ? getLinks(
                              'contact',
                              matter.parties
                                  .filter((item) => item.matterPartyRoleTypeCode === MatterPartyRoleType.OTHER_PARTY)
                                  .map((item) => item.party)
                          )
                        : '-',
                    label: isNew
                        ? infoLabel('matters.other-parties', 'matters.tooltips.other-contacts', 'justify-between')
                        : t('matters.other-parties'),
                    chips: partyChips(MatterPartyRoleType.OTHER_PARTY),
                },
                {
                    content: matter.parties
                        ? getLinks(
                              'contact',
                              matter.parties
                                  .filter((item) => item.matterPartyRoleTypeCode === MatterPartyRoleType.JUDGE)
                                  .map((item) => item.party)
                          )
                        : '-',
                    label: isNew
                        ? infoLabel('matters.judges', 'matters.tooltips.other-contacts', 'justify-between')
                        : t('matters.judges'),
                    chips: partyChips(MatterPartyRoleType.JUDGE),
                },
            ].filter((row) => (!isNew && matter.isNoOpponent ? row.label !== t('matters.opponents') : true)),
        [matter, isNew, partyChips]
    );

    // The new design draws the figure cards rounder and roomier.
    const statCardClass = isNew ? 'rounded-[10px]' : STAT_CARD_CLASS;
    const statContentClass = isNew ? 'px-5 py-4' : STAT_CONTENT_CLASS;
    const figures = (
        <>
            <TotalHours
                financialData={matter.financialData}
                className={statCardClass}
                contentClassName={statContentClass}
            />
            <TotalAmounts
                financialData={matter.financialData}
                currency={matter.currencyDetails?.originalCurrency}
                className={`${statCardClass} new:mt-(--card-gap) mt-4`}
                contentClassName={statContentClass}
            />
            <TotalTimecards
                financialData={matter.financialData}
                className={`${statCardClass} new:mt-(--card-gap) mt-4`}
                contentClassName={statContentClass}
                skip={matter.isNonBillable}
            />
            <TotalExpenseAmounts
                financialData={matter.financialData}
                uninvoicedExpensesAmount={matter.currencyDetails?.uninvoicedExpensesInOriginalCurrency}
                currency={matter.currencyDetails?.originalCurrency}
                className={`${statCardClass} new:mt-(--card-gap) mt-4`}
                contentClassName={statContentClass}
                skip={matter.isNonBillable}
            />
            <TotalExpenses
                financialData={matter.financialData}
                className={`${statCardClass} new:mt-(--card-gap) mt-4`}
                contentClassName={statContentClass}
                skip={matter.isNonBillable}
            />
            {matterBillingSummary !== undefined && matter.pricingLimitationTypeCode === 'HOURLY_CAPPED' ? (
                <CapFeeBudgetBox
                    variant="outlined"
                    className={`${statCardClass} new:mt-(--card-gap) mt-4`}
                    contentClassName="px-4 py-3"
                    budget={matterBillingSummary?.budget?.capTotal ?? 0}
                    remaining={matterBillingSummary?.budget?.remainingCap ?? 0}
                    used={matterBillingSummary?.budget?.usedCap ?? 0}
                    currency={matter.currencyDetails?.originalCurrency}
                    financialData={matter.financialData}
                />
            ) : null}
        </>
    );
    const blockedAlert = matter.isCurrentlyBlocked && (
        <DelegationAlert role="status" severity="blocked">
            <FormattedMessage id="dashboard.tooltips.timecard-blocked" />
        </DelegationAlert>
    );
    const auditInfo = (
        <AuditInfo
            createdAt={matter.createdAt}
            createdByEmployee={matter.createdByEmployee}
            lastModifiedAt={matter.lastModifiedAt}
            changedByEmployee={matter.changedByEmployee}
        />
    );

    if (isNew) {
        const overviewData = withMissing(getOverviewData(), missingFields);
        // The sections stacked on the left and the figures on the right: 2 : 1 from the open sidebar's 1280 px,
        // 3 : 2 from 1024 px, one column below.
        return (
            <div className={RECORD_GRID_CLASS}>
                <div className="flex min-w-0 flex-col gap-(--card-gap)">
                    {blockedAlert}
                    <MatterSection id="details" titleId="matters.overview" matter={matter} rows={overviewData}>
                        {(edit) => (
                            <GridItemList
                                isNew
                                titleId="matters.overview"
                                data={overviewData}
                                button={edit && <span data-tour="record-section-edit">{edit}</span>}
                            />
                        )}
                    </MatterSection>
                    <MatterSection id="team" titleId="matters.our-team" matter={matter} rows={teamRows(matter)}>
                        {(edit) => (
                            <TeamCard
                                matter={matter}
                                caseAssistantLabel={isAdministratorFieldEnabled ? caseAssistantLabel : null}
                                edit={edit}
                            />
                        )}
                    </MatterSection>
                    <MatterSection id="client-parties" titleId="matters.client-parties" matter={matter}>
                        {(edit) => (
                            <GridItemList
                                isNew
                                titleId="matters.client-parties"
                                data={withMissing(getClientData(), missingFields)}
                                button={edit}
                            />
                        )}
                    </MatterSection>
                    <MatterSection id="opponents" titleId="matters.opponents-targets-and-other" matter={matter}>
                        {(edit) => (
                            <GridItemList
                                isNew
                                titleId="matters.opponents-targets-and-other"
                                data={withMissing(getOtherData(), missingFields)}
                                button={edit}
                            />
                        )}
                    </MatterSection>
                    {auditInfo}
                </div>
                <div className="min-w-0">
                    {/* A draft has no time, expenses or invoices to count: whom it is shared with takes their place. */}
                    {matter.isDraft ? <DraftSharedWith /> : figures}
                </div>
            </div>
        );
    }

    return (
        <div className="grid grid-cols-1 gap-6 md:grid-cols-12">
            {matter.isDraft ? null : <div className="md:col-span-2">{figures}</div>}
            <div className={matter.isDraft ? 'md:col-span-12' : 'md:col-span-10'}>
                {blockedAlert && <div className="mb-6">{blockedAlert}</div>}

                <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                    <div>
                        <GridItemList
                            titleId="matters.overview"
                            size={12}
                            data={withMissing(getOverviewData(), missingFields)}
                            key="matter-overview"
                        />
                    </div>
                    <div>
                        <GridItemList
                            titleId="matters.our-team"
                            size={12}
                            data={withMissing(getEmployeeData(), missingFields)}
                            key="matter-staff"
                        />
                        {matter.isDraft ? (
                            <div className="mt-6">
                                <DraftSharedWith />
                            </div>
                        ) : null}
                        <GridItemList
                            titleId="matters.client-parties"
                            size={12}
                            data={withMissing(getClientData(), missingFields)}
                            key="matter-clients"
                            className="mt-6"
                        />
                    </div>
                </div>
                <GridItemList
                    titleId="matters.opponents-targets-and-other"
                    size={12}
                    data={withMissing(getOtherData(), missingFields)}
                    key="matter-other"
                    className="mt-6"
                />

                <div className="mt-6">{auditInfo}</div>
            </div>
        </div>
    );
};

export default GeneralView;
